import { Component, Input } from '@angular/core';
import { DwMediaRepositoryService } from '@devwareapps/devware-cap';
import { StructuredDocumentAssetModel } from '../../models/structured-document-viewer.model';

type StructuredDocumentListStyleMode = 'source' | 'aim';

@Component({
  selector: 'app-structured-document-content',
  templateUrl: './structured-document-content.component.html',
  styleUrls: ['./structured-document-content.component.scss']
})
export class StructuredDocumentContentComponent {
  private _rawBlocks: any[] = [];
  private _blocks: any[] = [];
  private _listDepth = 0;

  @Input()
  set blocks(value: any[]) {
    this._rawBlocks = this.asArray(value);
    this.refreshBlocks();
  }

  get blocks(): any[] {
    return this._blocks;
  }

  @Input()
  set listDepth(value: number) {
    const nextDepth = Number(value);
    this._listDepth = Number.isFinite(nextDepth) && nextDepth > 0 ? nextDepth : 0;
    this.refreshBlocks();
  }

  get listDepth(): number {
    return this._listDepth;
  }

  @Input() assets: StructuredDocumentAssetModel[] = [];
  @Input() listStyleMode: StructuredDocumentListStyleMode = 'source';

  constructor(private dwMediaRepository: DwMediaRepositoryService) {
  }

  trackByIndex(index: number): number {
    return index;
  }

  asArray(value: any): any[] {
    return Array.isArray(value) ? value : [];
  }

  joinText(nodes: any[]): string {
    return this.asArray(nodes)
      .map(node => this.inlineNodeToText(node))
      .join('');
  }

  inlineNodeToText(node: any): string {
    if (!node) {
      return '';
    }

    if (typeof node === 'string') {
      return node;
    }

    if (node.t === 'text') {
      return node.s || '';
    }

    if (node.t === 'image') {
      return node.alt || '';
    }

    if (node.t === 'br') {
      return ' ';
    }

    return this.joinText(node.c);
  }

  getAsset(assetKey: string): StructuredDocumentAssetModel | undefined {
    return (this.assets || []).find(asset => asset.AssetKey === assetKey);
  }

  getAssetUrl(assetKey: string): string {
    const mediaPath = this.getAsset(assetKey)?.MediaPath;
    return mediaPath ? this.dwMediaRepository.getImageUrlFromMediaPath(mediaPath) : '';
  }

  getImageSrc(block: any): string {
    if (!block) {
      return '';
    }

    return this.getAssetUrl(block.assetKey) || block.src || '';
  }

  isInternalDocumentHref(href: string): boolean {
    return typeof href === 'string' && href.startsWith('#');
  }

  private getCalloutTitle(block: any): string {
    const kindTitle = this.getCalloutKindTitle(block?.kind);
    return kindTitle || block?.title || '';
  }

  private normalizeBlocks(blocks: any[]): any[] {
    return this.asArray(blocks).map(block => this.normalizeBlock(block));
  }

  private refreshBlocks(): void {
    this._blocks = this.normalizeBlocks(this._rawBlocks);
  }

  private normalizeBlock(block: any): any {
    if (!block || typeof block !== 'object') {
      return block;
    }

    if (block.t === 'table') {
      return this.normalizeTableBlock(block);
    }

    if (block.t === 'callout') {
      const normalizedChildren = this.normalizeBlocks(block.c);
      const resolvedTitle = this.getCalloutTitle(block);

      return {
        ...block,
        title: resolvedTitle,
        c: this.stripLeadingCalloutLabel(resolvedTitle, normalizedChildren)
      };
    }

    if (block.t === 'ul' || block.t === 'ol') {
      const childListDepth = block.t === 'ol' ? this._listDepth + 1 : this._listDepth;

      return {
        ...block,
        _childListDepth: childListDepth,
        _listClass: this.resolveListClass(block, childListDepth),
        _listStyleType: this.resolveListStyleType(block, childListDepth),
        c: this.asArray(block.c).map((item: any) => ({
          ...item,
          c: this.normalizeBlocks(item?.c)
        }))
      };
    }

    return {
      ...block,
      c: this.normalizeInlineNodes(block.c)
    };
  }

  private normalizeTableBlock(block: any): any {
    if (this.hasStructuredTableRows(block?.head) || this.hasStructuredTableRows(block?.body) || this.hasStructuredTableRows(block?.foot)) {
      return block;
    }

    const legacyHeader = this.asArray(block?.header);
    const legacyBody = this.asArray(block?.body);

    return {
      ...block,
      head: legacyHeader.length
        ? [{ cells: legacyHeader.map((cell: any) => ({ t: 'th', c: this.asArray(cell) })) }]
        : [],
      body: legacyBody.map((row: any) => ({
        cells: this.asArray(row).map((cell: any) => ({ t: 'td', c: this.asArray(cell) }))
      })),
      foot: this.asArray(block?.foot)
    };
  }

  private isStructuredTableRows(rows: any): boolean {
    return this.asArray(rows).every((row: any) => !row || Array.isArray(row?.cells));
  }

  private hasStructuredTableRows(rows: any): boolean {
    const rowArray = this.asArray(rows);
    return rowArray.length > 0 && this.isStructuredTableRows(rowArray);
  }

  private stripLeadingCalloutLabel(title: string | null | undefined, blocks: any[]): any[] {
    const normalizedTitle = this.normalizeCalloutLabel(title);
    if (!normalizedTitle || !blocks.length) {
      return blocks;
    }

    const [firstBlock, ...remainingBlocks] = blocks;
    if (!firstBlock || firstBlock.t !== 'p') {
      return blocks;
    }

    const firstBlockText = this.normalizeCalloutLabel(this.joinText(firstBlock.c));
    if (firstBlockText !== normalizedTitle) {
      return blocks;
    }

    return remainingBlocks;
  }

  private normalizeCalloutLabel(value: string | null | undefined): string {
    return (value || '')
      .replace(/[:\-\s]+$/g, '')
      .trim()
      .toLowerCase();
  }

  private getCalloutKindTitle(kind: string | null | undefined): string {
    switch ((kind || '').trim().toLowerCase()) {
      case 'note':
        return 'Note';
      case 'caution':
        return 'Caution';
      case 'warning':
        return 'Warning';
      default:
        return '';
    }
  }

  private normalizeInlineNodes(nodes: any): any[] {
    const normalizedNodes = this.asArray(nodes).map(node => this.normalizeInlineNode(node));
    const result: any[] = [];

    normalizedNodes.forEach((node, index) => {
      if (!node) {
        return;
      }

      result.push(node);

      const nextNode = normalizedNodes[index + 1];
      if (this.shouldInsertInlineSpace(node, nextNode)) {
        result.push({ t: 'text', s: ' ' });
      }
    });

    return result;
  }

  private resolveListClass(block: any, listDepth: number): string {
    const classes = ['sdc-list', block?.t === 'ol' ? 'sdc-list--ordered' : 'sdc-list--unordered'];
    const styleType = this.resolveEffectiveListStyleType(block, listDepth);
    const markerFormat = this.resolveEffectiveListMarkerFormat(block, listDepth);

    if (styleType) {
      classes.push(`sdc-list--style-${styleType}`);
    }

    if (markerFormat) {
      classes.push(`sdc-list--marker-${markerFormat}`);
    }

    return classes.join(' ');
  }

  private resolveListStyleType(block: any, listDepth: number): string | null {
    const markerFormat = this.resolveEffectiveListMarkerFormat(block, listDepth);
    if (markerFormat === 'paren' || markerFormat === 'bracket') {
      return null;
    }

    return this.resolveEffectiveListStyleType(block, listDepth) || null;
  }

  private resolveEffectiveListStyleType(block: any, listDepth: number): string {
    if (this.listStyleMode === 'aim' && block?.t === 'ol') {
      return listDepth % 2 === 0 ? 'decimal' : 'lower-alpha';
    }

    const styleType = (block?.styleType || '').toString().trim().toLowerCase();
    if (styleType) {
      return styleType;
    }

    if (block?.t !== 'ol') {
      return '';
    }

    return listDepth % 2 === 0 ? 'decimal' : 'lower-alpha';
  }

  private resolveEffectiveListMarkerFormat(block: any, listDepth: number): string {
    if (this.listStyleMode === 'aim' && block?.t === 'ol') {
      if (listDepth >= 5) {
        return 'bracket';
      }

      return listDepth >= 3 ? 'paren' : 'plain';
    }

    const markerFormat = (block?.markerFormat || '').toString().trim().toLowerCase();
    if (markerFormat) {
      return markerFormat;
    }

    if (block?.t !== 'ol') {
      return '';
    }

    if (listDepth >= 5) {
      return 'bracket';
    }

    return listDepth >= 3 ? 'paren' : 'plain';
  }

  private normalizeInlineNode(node: any): any {
    if (!node || typeof node !== 'object') {
      return node;
    }

    if (!Array.isArray(node.c)) {
      return node;
    }

    return {
      ...node,
      c: this.normalizeInlineNodes(node.c)
    };
  }

  private shouldInsertInlineSpace(leftNode: any, rightNode: any): boolean {
    if (!leftNode || !rightNode) {
      return false;
    }

    if (['br', 'sup', 'sub'].includes(leftNode.t) || ['br', 'sup', 'sub'].includes(rightNode.t)) {
      return false;
    }

    const leftText = this.inlineNodeToText(leftNode);
    const rightText = this.inlineNodeToText(rightNode);
    if (!leftText || !rightText) {
      return false;
    }

    if (/\s$/.test(leftText) || /^\s/.test(rightText)) {
      return false;
    }

    return /[A-Za-z0-9]$/.test(leftText) && /^[A-Za-z0-9]/.test(rightText);
  }
}
